import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Download, Loader2, Mail, XCircle } from "lucide-react";
import { API, api, fmtPrice } from "@/lib/api";
import { usePageTitle } from "@/lib/content";

export default function CheckoutSuccess() {
  usePageTitle("Order Confirmed — Sitebound", "Your Sitebound pack is ready to download.");
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("verifying");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let tries = 0;
    const timer = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data);
          setState("paid");
          clearInterval(timer);
        } else if (tries >= 20) {
          setState("timeout");
          clearInterval(timer);
        }
      } catch {
        if (tries >= 20) {
          setState("error");
          clearInterval(timer);
        }
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [sessionId]);

  return (
    <div data-testid="checkout-success-page" className="blueprint-grid">
      <div className="mx-auto flex min-h-[70vh] max-w-3xl flex-col items-center justify-center px-4 py-24 text-center sm:px-6">
        {state === "verifying" && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-[#FF5500]" />
            <h1 className="font-display mt-8 text-4xl font-extrabold uppercase tracking-tight text-slate-100">
              Confirming your payment
            </h1>
            <p className="mt-4 text-sm text-slate-400">
              Hold tight — we're checking with Stripe. This takes a few seconds.
            </p>
          </>
        )}

        {state === "paid" && order && (
          <>
            <CheckCircle2 data-testid="payment-success-icon" className="h-14 w-14 text-[#FF5500]" />
            <div className="mono-label mt-6">Order {order.order_id}</div>
            <h1 className="font-display mt-4 text-5xl font-extrabold uppercase leading-[0.9] tracking-tight text-slate-100 sm:text-6xl">
              You're in.
            </h1>
            <p className="mt-5 max-w-md text-base leading-relaxed text-slate-400">
              <span className="font-semibold text-slate-200">{order.product_name}</span> —{" "}
              {fmtPrice(order.amount)} paid. Your pack is ready right now.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <a
                data-testid="download-pack-button"
                href={`${API}/downloads/${order.download_token}`}
                className="flex items-center gap-3 border border-[#FF5500] px-8 py-4 text-sm font-bold uppercase tracking-widest text-[#FF5500] transition-colors hover:bg-[#FF5500] hover:text-white"
              >
                <Download className="h-4 w-4" /> Download ZIP backup
              </a>
              <Link
                data-testid="success-hub-link"
                to="/hub"
                className="flex items-center gap-3 bg-[#FF5500] px-8 py-4 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E04800]"
              >
                Open your Job Seeker Hub
              </Link>
            </div>
            {order.email_sent && (
              <p data-testid="email-sent-note" className="mt-6 flex items-center gap-2 text-xs text-slate-500">
                <Mail className="h-3.5 w-3.5 text-[#FF5500]" />
                We've emailed you a link to your Job Seeker Hub — check your inbox (and junk folder).
              </p>
            )}
            <Link
              data-testid="success-back-home"
              to="/"
              className="mt-10 text-xs font-semibold uppercase tracking-widest text-slate-400 hover:text-[#FF5500]"
            >
              Back to Sitebound
            </Link>
          </>
        )}

        {(state === "timeout" || state === "error") && (
          <>
            <XCircle className="h-14 w-14 text-slate-500" />
            <h1 className="font-display mt-8 text-4xl font-extrabold uppercase tracking-tight text-slate-100">
              {state === "timeout" ? "Still processing" : "Something's off"}
            </h1>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-400">
              {state === "timeout"
                ? "Your payment is taking longer than usual to confirm. If you've been charged, your pack will be available in your Job Seeker Hub as soon as the payment is confirmed."
                : "We couldn't confirm this order. If you've been charged, contact us and we'll sort it out straight away."}
            </p>
            <Link
              data-testid="success-contact-link"
              to="/contact"
              className="mt-8 border border-white/25 px-7 py-3.5 text-sm font-bold uppercase tracking-widest text-slate-100 hover:border-[#FF5500] hover:text-[#FF5500]"
            >
              Contact us
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
